import { useQueries } from '@tanstack/react-query';
import { FinancialItems } from 'src/financial/financial-data/hooks/useFinancialItems';
import {
  FinancialCategory,
  buildCategories,
  emptyGroupedRows,
  extractRows,
  mergeGroupedRows,
} from 'src/financial/financial-data/rework/financialRows';
import { FamilyMember } from './useFamilyMembers';

export function useFamilyFinancialItems(members: FamilyMember[]): {
  categories: FinancialCategory[];
  isLoading: boolean;
  hasErrors: boolean;
} {
  const results = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'financial-items-bff'],
      enabled: !!member.clientId,
    })),
  });

  const isLoading = results.some(result => result.isLoading);
  const hasErrors = results.some(result => result.isError);

  // The family view has no "me", so an item shows every owner it records rather than one
  // member's share. Names come from the family roster, keyed by client.
  const nameByClientId = new Map(
    members.map(member => [
      member.clientId,
      `${member.client.firstName} ${member.client.lastName}`.trim(),
    ]),
  );
  const resolveOwnerName = (clientId: string) => nameByClientId.get(clientId);

  const grouped = members.map((member, index) => {
    const data = results[index].data as FinancialItems | undefined;
    if (!data) return emptyGroupedRows();
    // Full name throughout: rows that record owners list full names, so the fallback has to
    // match or the column reads as a mix of "Alice Smith" and "Alice".
    const memberName = nameByClientId.get(member.clientId) ?? member.client.firstName;
    return extractRows(data, memberName, undefined, resolveOwnerName);
  });

  const categories = buildCategories(mergeGroupedRows(grouped));

  return { categories, isLoading, hasErrors };
}
